import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useAuth } from '../context/UnifiedAuthContext';
import { getImageUrl } from '../utils/apiConfig';
import apiClient from '../utils/apiClient';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';


const PlayerProfileModal = ({ player, onClose, isOpen = true }) => {
  const { user, isAuthenticated, linkPlayer, userVersion } = useAuth();
  const [isLinked, setIsLinked] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkingError, setLinkingError] = useState('');
  const [showHistorical, setShowHistorical] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Check if the current user is linked to this player
  useEffect(() => {
    if (user && player) {
      // Check for linked player using either player_id field or player object
      const userPlayerId = user.player_id || user.player?.id;
      const linked = !!(userPlayerId && player.id && userPlayerId === player.id);
      setIsLinked(linked);
    }
  }, [user, player, userVersion]); // Include userVersion to force re-evaluation

  // Initialize tippy for tooltips
  useEffect(() => {
    tippy('[data-tippy-content]');
  }, [isLinked]);

  // Fetch available players for linking - only if user has no linked player at all
  useEffect(() => {
    if (isAuthenticated && !user?.player_id && !user?.player?.id) {
      fetchAvailablePlayers();
    }
  }, [isAuthenticated, user?.player_id, user?.player?.id]);

  const fetchAvailablePlayers = async () => {
    try {
      const response = await apiClient.get('/players');
      setAvailablePlayers(response.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const handleLinkPlayer = async () => {
    if (!selectedPlayerId) return;

    setIsLinking(true);
    setLinkingError('');

    try {
      const result = await linkPlayer(selectedPlayerId);
      if (result.success) {
        // Don't manually set isLinked - let useEffect handle it based on updated user data
        setSelectedPlayerId('');
      } else {
        setLinkingError(result.error || 'Failed to link player');
      }
    } catch (error) {
      setLinkingError('Failed to link player');
      console.error('Error linking player:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const getPlayerImageUrl = (player) => {
    // If this is the logged-in user's profile and they have a profile picture, use that
    if (isLinked && user?.profilePicture) {
      return getImageUrl(user.profilePicture);
    }

    // Otherwise use the player's image - check both image_path and image fields for compatibility
    const playerImage = player?.image_path || player?.image;
    if (player?.image && !player?.image_path) {
      // Handle legacy image field (filename only)
      return getImageUrl(`/uploads/players/${player.image}`);
    }
    return getImageUrl(playerImage);
  };

  if (!player) return null;

  return (
    <>
      <Modal onClose={onClose} isOpen={isOpen}>
        <div className="relative p-6 max-w-2xl mx-auto">
          {/* Account Linkage Section - Only show for your own profile or if you have no linked player */}
          {isAuthenticated && (isLinked || (!user?.player_id && !user?.player?.id)) && (
            <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-200">

              {isLinked ? (
                // This is YOUR linked profile
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-green-700 font-medium">
                      ✓ This is your player profile
                    </p>
                    <p className="text-sm text-green-600">
                      Logged in as: {user.email || user.username || 'User'}
                    </p>
                  </div>
                </div>
              ) : (
                // User has no linked player at all - show linking options
                <div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <p className="text-yellow-700">
                      Link your account to claim this player profile
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Link to Player
                      </label>
                      <select
                        value={selectedPlayerId}
                        onChange={(e) => setSelectedPlayerId(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Select a player to link</option>
                        {availablePlayers.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {linkingError && (
                      <p className="text-red-600 text-sm">{linkingError}</p>
                    )}

                    <button
                      onClick={handleLinkPlayer}
                      disabled={!selectedPlayerId || isLinking}
                      className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {isLinking ? 'Linking...' : 'Link Account'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Player Profile Information */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <img
                src={getPlayerImageUrl(player)}
                alt={player.name}
                className="w-36 h-36 rounded-full mx-auto mb-4 transition-transform transform hover:scale-105 cursor-pointer"
                onClick={() => setShowImageModal(true)}
                title="Click to view larger image"
              />
            </div>
            <h2 className="text-xl font-bold">{player.name}</h2>
            <p className="text-gray-500 text-sm">{player.email}</p>
            <p className="text-gray-500 text-sm">{player.phone_number}</p>
          </div>

          {/* Current Season Stats */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2 border-b pb-2">
              Current Season Stats
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p><strong>Current Handicap:</strong> {player.current_quota || 'N/A'}</p>
                <p><strong>Events Played:</strong> {player.events_played || 0}</p>
                <p><strong>Total Money Won:</strong> ${Number(player.money_won || 0).toFixed(2)}</p>
                <p><strong>Season Rank:</strong> #{player.rank || 'N/A'}</p>
              </div>
              <div>
                <p><strong>Total CTPs:</strong> {player.ctps || 0}</p>
                <p><strong>Total Skins:</strong> {player.skins || 0}</p>
                <p><strong>Wins:</strong> {player.wins || 0}</p>
                <p><strong>Top 3 Finishes:</strong> {player.top_3 || 0}</p>
              </div>
            </div>
            <div className="mt-3">
              <p><strong>Total Points:</strong> <span className="text-lg font-bold text-blue-600">{Number(player.total_points || 0).toFixed(0)}</span></p>
            </div>
          </div>

          {/* Collapsible Historical Stats */}
          <div>
            <h3
              className="text-lg font-semibold text-gray-700 mb-2 border-b pb-2 cursor-pointer flex items-center justify-between hover:text-blue-600"
              onClick={() => setShowHistorical(!showHistorical)}
            >
              Historical Stats
              <span className="text-blue-500">{showHistorical ? '▼' : '►'}</span>
            </h3>
            {showHistorical && (
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Career Events:</strong> {player.events_played || 0}</p>
                    <p><strong>Career Earnings:</strong> ${Number(player.money_won || 0).toFixed(2)}</p>
                    <p><strong>Career CTPs:</strong> {player.ctps || 0}</p>
                  </div>
                  <div>
                    <p><strong>Career Skins:</strong> {player.skins || 0}</p>
                    <p><strong>Top 10 Finishes:</strong> {player.top_10 || 0}</p>
                    <p><strong>Win Percentage:</strong> {player.events_played > 0 ? ((player.wins || 0) / player.events_played * 100).toFixed(1) : 0}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Image Expansion Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 text-xl"
              title="Close"
            >
              ×
            </button>
            <img
              src={getPlayerImageUrl(player)}
              alt={`${player.name} - Full Size`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PlayerProfileModal;
